import React, {Component} from "react"
import {set} from "lodash"

import Loading from "@irati/ui/src/Loading"
import {injectIntl, InjectIntl} from "@irati/utils/lang"
import {waitPromise} from "@irati/utils/promise"
import stepsStory, {anyStoriesOpen} from "@irati/utils/modalStory"
import companyBenefitsService from "@irati/deals/services/companyBenefits"
import authentication from "@irati/auth"
import {Deal, UserSaving} from "@irati/types"
import FullModal from "../../FullModal"
import Card from "./Card"
import Description from "./Description"
import Instructions from "./Instructions"
import SuccessStep from "./SuccessStep"

export const CARD = "CARD"
export const DESCRIPTION = "DESCRIPTION"
export const INSTRUCTIONS = "INSTRUCTIONS"
export const SUCCESS = "SUCCESS"

const steps = [CARD, DESCRIPTION, INSTRUCTIONS, SUCCESS]

interface EmptyCompanyBenefit {
  header: string | null
  savings: UserSaving
  cardImage: string | null
  description: string | null
  importantInfo: string[]
  redemptionInstructions: string
  contactInfo: string | null
}

const EMPTY_COMPANY_BENEFIT = (): EmptyCompanyBenefit => ({
  header: null,
  savings: {
    userSavings: {
      value: null,
      type: null,
      header: null
    }
  },
  cardImage: null,
  description: null,
  importantInfo: [],
  redemptionInstructions: "- ",
  contactInfo: null
})

interface Props extends InjectIntl {
  benefit?: Deal | EmptyCompanyBenefit
  onChange?: (benefit?: Deal, action?: string) => void
  hideBannerImageUpload?: boolean
  edit: boolean
}

interface Steps {
  [key: string]: boolean | undefined
}

interface State {
  show: Steps
  benefit: Deal | EmptyCompanyBenefit
  edit: boolean
  imageEdited: boolean
  load?: Promise<any>
  locale?: string
}

class CompanyBenefitsPopup extends Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      show: stepsStory(steps, null),
      benefit: this.props.benefit || EMPTY_COMPANY_BENEFIT(),
      edit: false,
      imageEdited: false,
      locale: authentication.userLocale()
    }
  }

  setBenefit = (benefit: Deal) => {
    const load = companyBenefitsService.getForAdmin(benefit.uuid).then((fullBenefit) => {
      this.setState({benefit: fullBenefit, edit: true})
    })
    this.setState({load})
  }

  setEdit = (edit: boolean) => {
    this.setState({edit})
  }

  show = (stepToShow = CARD) => {
    this.setState(({show, benefit}) => ({
      show: stepsStory(steps, stepToShow),
      benefit: anyStoriesOpen(show) ? benefit : EMPTY_COMPANY_BENEFIT()
    }))
  }

  hide = () => this.setState({show: stepsStory(steps, null)})

  onChange = (property: string, value: string) => {
    const {benefit} = this.state
    set(benefit, property, value)

    this.setState((prevState) => ({
      benefit: {
        ...prevState.benefit,
        ...benefit
      }
    }))
  }

  handleSubmit = () => {
    const {benefit, edit, locale} = this.state
    const {onChange = () => {}} = this.props

    const localBenefit = {
      ...benefit,
      locale
    }

    if (edit) {
      return companyBenefitsService.update(benefit.uuid, localBenefit as Deal).then((updatedBenefit) => {
        this.setState({show: stepsStory(steps, SUCCESS), edit: false})
        onChange(updatedBenefit, "edit")
      })
    }

    return companyBenefitsService.create(localBenefit as Deal).then((newBenefit) => {
      this.setState({show: stepsStory(steps, SUCCESS), edit: false})
      return waitPromise(2000).then(() => onChange(newBenefit))
    })
  }

  render() {
    const {show, benefit, load} = this.state
    const {hideBannerImageUpload} = this.props
    if (!benefit) {
      return null
    }

    return (
      <FullModal show={anyStoriesOpen(show)} onHide={this.hide}>
        <Loading load={load}>
          <Card
            show={show[CARD]}
            selectStep={this.show}
            onChange={this.onChange}
            benefit={benefit}
            hideBannerImageUpload={hideBannerImageUpload}
          />
        </Loading>
        <Description show={show[DESCRIPTION]} selectStep={this.show} onChange={this.onChange} benefit={benefit} />
        <Instructions
          show={show[INSTRUCTIONS]}
          selectStep={this.show}
          onChange={this.onChange}
          onSubmit={this.handleSubmit}
          benefit={benefit}
        />
        <SuccessStep show={show[SUCCESS]} onHide={this.hide} />
      </FullModal>
    )
  }
}

export default injectIntl(CompanyBenefitsPopup, {withRef: true})
